// Phase A — Canteen Rules admin.
//
// Two cards stacked:
//   1. Canteens — physical canteens (worker mess, staff mess, etc.)
//   2. Coupon Rules — subsidy + eligibility rules. Admin can scope by
//      employee_category / department / shift / canteen / coupon_type.
//      Subsidy + deduction must sum to 100 (DB constraint).

import { useMemo, useState } from 'react'
import {
  App, Button, Card, DatePicker, Form, Input, InputNumber, Modal, Popconfirm,
  Select, Space, Switch, Table, Tag, TimePicker, Tooltip, Typography,
} from 'antd'
import {
  CoffeeOutlined, DeleteOutlined, EditOutlined, PlusOutlined,
  ShopOutlined,
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'

import {
  welfareMastersApi, type Canteen, type CouponRule, type CouponType,
} from '@/features/hr/welfareMastersApi'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'

const { Text } = Typography


export default function CanteenRulesPage() {
  return (
    <div>
      <PageHeader
        title="Canteen Coupon Rules"
        subtitle="Define canteens and their subsidy rules. Worker share + company share must total 100%."
      />
      <CanteensCard />
      <CouponRulesCard />
    </div>
  )
}


// ─── Canteens card ────────────────────────────────────────────────────

function CanteensCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const { plantId } = usePlantId()
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<Canteen | null>(null)

  const { data: canteens, isLoading } = useQuery({
    queryKey: ['canteens', plantId],
    queryFn: () => welfareMastersApi.listCanteens({
      plant_id: plantId ?? undefined,
    }),
    enabled: !!plantId,
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['canteens'] })

  const createMut = useMutation({
    mutationFn: welfareMastersApi.createCanteen,
    onSuccess: () => { message.success('Canteen added'); invalidate(); setCreateOpen(false) },
    onError: errToast(message),
  })
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      welfareMastersApi.patchCanteen(id, body),
    onSuccess: () => { message.success('Saved'); invalidate(); setEditing(null) },
    onError: errToast(message),
  })

  return (
    <Card
      style={{ marginTop: 16 }}
      title={<Space><ShopOutlined /> Canteens</Space>}
      extra={canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          Add Canteen
        </Button>
      )}
    >
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={false}
        columns={[
          {
            title: 'Code', dataIndex: 'canteen_code', key: 'code', width: 110,
            render: (s: string) => <Text code>{s}</Text>,
          },
          { title: 'Name', dataIndex: 'canteen_name', key: 'name' },
          { title: 'Location', dataIndex: 'location', key: 'loc',
            render: (v: string | null) => v ?? '—' },
          {
            title: 'Status', dataIndex: 'is_active', key: 'st', width: 100,
            render: (b: boolean) => (
              <Tag color={b ? 'success' : 'default'}>
                {b ? 'ACTIVE' : 'INACTIVE'}
              </Tag>
            ),
          },
          ...(canEdit ? [{
            title: '', key: 'act', width: 50,
            render: (_: unknown, r: Canteen) => (
              <Button size="small" type="text" icon={<EditOutlined />}
                onClick={() => setEditing(r)} />
            ),
          }] : []),
        ] as never}
        dataSource={canteens ?? []}
      />
      <CanteenFormModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={(body) => createMut.mutate({ ...body, plant_id: plantId })}
        submitting={createMut.isPending}
      />
      <CanteenFormModal
        open={!!editing}
        canteen={editing}
        onClose={() => setEditing(null)}
        onSubmit={(body) => editing && patchMut.mutate({ id: editing.id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}


function CanteenFormModal({
  open, canteen, onClose, onSubmit, submitting,
}: {
  open: boolean
  canteen?: Canteen | null
  onClose: () => void
  onSubmit: (body: Record<string, unknown>) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const isEdit = !!canteen
  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title={isEdit ? `Edit ${canteen?.canteen_name}` : 'Add Canteen'}
      okText={isEdit ? 'Save' : 'Add'} confirmLoading={submitting} width={460}
      onOk={() => form.validateFields().then(onSubmit)}
    >
      <Form form={form} layout="vertical" initialValues={canteen ?? {}}>
        <Form.Item name="canteen_code" label="Canteen Code"
          rules={[{ required: true, min: 2, max: 30 }]}>
          <Input placeholder="CAN-WORKER" disabled={isEdit} />
        </Form.Item>
        <Form.Item name="canteen_name" label="Canteen Name"
          rules={[{ required: true }]}>
          <Input placeholder="Main Worker Canteen" />
        </Form.Item>
        <Form.Item name="location" label="Location">
          <Input placeholder="Block A, ground floor" />
        </Form.Item>
        {isEdit && (
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


// ─── Coupon rules card ────────────────────────────────────────────────

function CouponRulesCard() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const canEdit = usePermission('employee.create')
  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<CouponRule | null>(null)

  const { data: rules, isLoading } = useQuery({
    queryKey: ['coupon-rules'],
    queryFn: () => welfareMastersApi.listCouponRules(),
  })
  const { data: canteens } = useQuery({
    queryKey: ['canteens-for-rules'],
    queryFn: () => welfareMastersApi.listCanteens(),
  })

  const invalidate = () => qc.invalidateQueries({ queryKey: ['coupon-rules'] })

  const createMut = useMutation({
    mutationFn: welfareMastersApi.createCouponRule,
    onSuccess: () => { message.success('Rule added'); invalidate(); setCreateOpen(false) },
    onError: errToast(message),
  })
  const patchMut = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      welfareMastersApi.patchCouponRule(id, body),
    onSuccess: () => { message.success('Saved'); invalidate(); setEditing(null) },
    onError: errToast(message),
  })
  const delMut = useMutation({
    mutationFn: welfareMastersApi.deleteCouponRule,
    onSuccess: () => { message.success('Deleted'); invalidate() },
    onError: errToast(message),
  })

  const canteensById = useMemo(() => {
    const m = new Map<string, Canteen>()
    ;(canteens ?? []).forEach((c) => m.set(c.id, c))
    return m
  }, [canteens])

  return (
    <Card
      style={{ marginTop: 16 }}
      title={<Space><CoffeeOutlined /> Coupon Rules</Space>}
      extra={canEdit && (
        <Button type="primary" icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}>
          Add Rule
        </Button>
      )}
    >
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        pagination={{ pageSize: 25 }}
        columns={[
          {
            title: 'Code', dataIndex: 'rule_code', key: 'rc', width: 160,
            render: (s: string, r: CouponRule) => (
              <Space size={4} direction="vertical">
                <Text code style={{ fontSize: 11 }}>{s}</Text>
                {r.rule_name && (
                  <Text type="secondary" style={{ fontSize: 11 }}>{r.rule_name}</Text>
                )}
              </Space>
            ),
          },
          {
            title: 'Scope', key: 'scope',
            render: (_: unknown, r: CouponRule) => (
              <Space size={4} wrap>
                <Tag color="blue">{r.employee_category ?? 'ANY CAT'}</Tag>
                {r.canteen_id && (
                  <Tag>{canteensById.get(r.canteen_id)?.canteen_name ?? '—'}</Tag>
                )}
                <Tag color="purple">{r.coupon_type}</Tag>
              </Space>
            ),
          },
          {
            title: 'Per day', dataIndex: 'coupons_per_day', key: 'cpd', width: 70,
            render: (v: number) => <Tag>{v}</Tag>,
          },
          {
            title: 'Rate / Split', key: 'rate', width: 200,
            render: (_: unknown, r: CouponRule) => {
              const total = Number(r.rate_per_coupon)
              const sub = Number(r.company_subsidy_percent)
              const ded = Number(r.employee_deduction_percent)
              return (
                <Space size={4} direction="vertical">
                  <Text strong>₹{total.toFixed(2)}</Text>
                  <Tooltip title={`${sub}% co. + ${ded}% emp = 100%`}>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Co. ₹{((total * sub) / 100).toFixed(2)}
                      {' · '}
                      Emp ₹{((total * ded) / 100).toFixed(2)}
                    </Text>
                  </Tooltip>
                </Space>
              )
            },
          },
          {
            title: 'Window', key: 'win', width: 130,
            render: (_: unknown, r: CouponRule) => {
              if (!r.meal_window_start) return <Text type="secondary">—</Text>
              return (
                <Text style={{ fontSize: 11 }}>
                  {r.meal_window_start.slice(0, 5)} – {r.meal_window_end?.slice(0, 5) ?? '?'}
                </Text>
              )
            },
          },
          {
            title: 'Active', dataIndex: 'is_active', key: 'a', width: 80,
            render: (b: boolean) => (
              <Tag color={b ? 'success' : 'default'}>{b ? 'YES' : 'NO'}</Tag>
            ),
          },
          ...(canEdit ? [{
            title: '', key: 'act', width: 80,
            render: (_: unknown, r: CouponRule) => (
              <Space size={0}>
                <Button size="small" type="text" icon={<EditOutlined />}
                  onClick={() => setEditing(r)} />
                <Popconfirm title="Delete this rule?"
                  onConfirm={() => delMut.mutate(r.id)}>
                  <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          }] : []),
        ] as never}
        dataSource={rules ?? []}
      />
      <CouponRuleFormModal
        open={createOpen}
        canteens={canteens ?? []}
        onClose={() => setCreateOpen(false)}
        onSubmit={createMut.mutate}
        submitting={createMut.isPending}
      />
      <CouponRuleFormModal
        open={!!editing}
        rule={editing}
        canteens={canteens ?? []}
        onClose={() => setEditing(null)}
        onSubmit={(body) => editing && patchMut.mutate({ id: editing.id, body })}
        submitting={patchMut.isPending}
      />
    </Card>
  )
}


function CouponRuleFormModal({
  open, rule, canteens, onClose, onSubmit, submitting,
}: {
  open: boolean
  rule?: CouponRule | null
  canteens: Canteen[]
  onClose: () => void
  onSubmit: (body: Record<string, unknown>) => void
  submitting: boolean
}) {
  const [form] = Form.useForm()
  const isEdit = !!rule

  // Live subsidy/deduction split — keeps the math always = 100
  const [subsidy, setSubsidy] = useState<number>(
    rule ? Number(rule.company_subsidy_percent) : 60)

  return (
    <Modal
      open={open} onCancel={onClose} destroyOnClose
      title={isEdit ? `Edit ${rule?.rule_code}` : 'Add Coupon Rule'}
      okText={isEdit ? 'Save' : 'Add'} confirmLoading={submitting} width={620}
      onOk={() => form.validateFields().then((v) => {
        const body: Record<string, unknown> = {
          ...v,
          // Dates: serialize
          effective_from: (v.effective_from as Dayjs).format('YYYY-MM-DD'),
          effective_to: v.effective_to ? (v.effective_to as Dayjs).format('YYYY-MM-DD') : null,
          // Times: HH:mm:ss
          meal_window_start: v.meal_window_start
            ? (v.meal_window_start as Dayjs).format('HH:mm:ss') : null,
          meal_window_end: v.meal_window_end
            ? (v.meal_window_end as Dayjs).format('HH:mm:ss') : null,
          company_subsidy_percent: subsidy.toFixed(2),
          employee_deduction_percent: (100 - subsidy).toFixed(2),
        }
        onSubmit(body)
      })}
    >
      <Form form={form} layout="vertical" initialValues={rule ? {
        ...rule,
        effective_from: dayjs(rule.effective_from),
        effective_to: rule.effective_to ? dayjs(rule.effective_to) : null,
        meal_window_start: rule.meal_window_start
          ? dayjs(rule.meal_window_start, 'HH:mm:ss') : null,
        meal_window_end: rule.meal_window_end
          ? dayjs(rule.meal_window_end, 'HH:mm:ss') : null,
        rate_per_coupon: Number(rule.rate_per_coupon),
      } : {
        coupon_type: 'LUNCH',
        coupons_per_day: 1,
        rate_per_coupon: 50,
        effective_from: dayjs(),
      }}>
        <Space.Compact block>
          <Form.Item name="rule_code" label="Rule Code"
            rules={[{ required: true, min: 2 }]} style={{ flex: 1, marginRight: 8 }}>
            <Input placeholder="WORKER_LUNCH" disabled={isEdit} />
          </Form.Item>
          <Form.Item name="rule_name" label="Display Name" style={{ flex: 1 }}>
            <Input placeholder="Worker daily lunch" />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="employee_category" label="Employee Category"
            style={{ flex: 1, marginRight: 8 }}>
            <Select allowClear placeholder="any" options={[
              { label: 'Worker', value: 'WORKER' },
              { label: 'Staff', value: 'STAFF' },
              { label: 'Contract', value: 'CONTRACT' },
              { label: 'Trainee', value: 'TRAINEE' },
            ]} />
          </Form.Item>
          <Form.Item name="canteen_id" label="Canteen" style={{ flex: 1 }}>
            <Select allowClear placeholder="any" options={canteens.map((c) => ({
              label: c.canteen_name, value: c.id,
            }))} />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="coupon_type" label="Meal"
            rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
            <Select options={[
              { label: 'Breakfast', value: 'BREAKFAST' },
              { label: 'Lunch', value: 'LUNCH' },
              { label: 'Dinner', value: 'DINNER' },
              { label: 'Tea / Snacks', value: 'TEA_SNACKS' },
              { label: 'Full day', value: 'FULL_DAY' },
              { label: 'Custom', value: 'CUSTOM' },
            ]} />
          </Form.Item>
          <Form.Item name="coupons_per_day" label="Per day"
            style={{ flex: 1, marginRight: 8 }}>
            <InputNumber min={0} max={10} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="coupons_per_week" label="Per week (cap)" style={{ flex: 1 }}>
            <InputNumber min={0} max={50} style={{ width: '100%' }} placeholder="any" />
          </Form.Item>
        </Space.Compact>
        <Form.Item name="rate_per_coupon" label="Rate per coupon (₹)"
          rules={[{ required: true }]}>
          <InputNumber min={0} step={5} style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label="Subsidy split">
          <Space size={8} align="center" style={{ width: '100%' }}>
            <Text style={{ width: 75 }}>Company:</Text>
            <InputNumber min={0} max={100} value={subsidy}
              onChange={(v) => setSubsidy(Number(v ?? 0))}
              addonAfter="%" style={{ width: 100 }} />
            <Text style={{ marginLeft: 16, width: 75 }}>Worker:</Text>
            <InputNumber value={100 - subsidy} disabled
              addonAfter="%" style={{ width: 100 }} />
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              Must sum to 100%
            </Text>
          </Space>
        </Form.Item>
        <Space.Compact block>
          <Form.Item name="meal_window_start" label="Meal window start"
            style={{ flex: 1, marginRight: 8 }}>
            <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="meal_window_end" label="Meal window end" style={{ flex: 1 }}>
            <TimePicker format="HH:mm" minuteStep={5} style={{ width: '100%' }} />
          </Form.Item>
        </Space.Compact>
        <Space.Compact block>
          <Form.Item name="effective_from" label="Effective from"
            rules={[{ required: true }]} style={{ flex: 1, marginRight: 8 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="effective_to" label="Effective to (optional)" style={{ flex: 1 }}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Space.Compact>
        {isEdit && (
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
        <Form.Item name="remarks" label="Remarks">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
    </Modal>
  )
}


// ─── Helpers ──────────────────────────────────────────────────────────

function errToast(message: ReturnType<typeof App.useApp>['message']) {
  return (e: unknown) => {
    const err = e as { response?: { data?: { detail?: string } } }
    message.error(err?.response?.data?.detail ?? 'Save failed')
  }
}
